/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { FormMap, throttledSyncForms } from './actions';
import { FormDefinitionWrapper, FormDefinition } from '../../react/components/form/services/form';
import moment from "moment";
import { combineEpics } from "redux-observable";
import { empty, from, of } from "rxjs";
import { ajax, AjaxError } from "rxjs/ajax";
import { catchError, concatMap, debounceTime, exhaustMap, expand, filter, groupBy, map, mergeMap, pluck, reduce, switchMap, tap, throttleTime, toArray, withLatestFrom } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import config, { appVersion } from "../../config";
import { DairyFARMDB, getDB, withRetry_ } from "../../db/store";
import { FORMS_STORE, LAST_FORM_CHECK_KEY } from "../../db/stores/forms";
import { createEpic, getCommonHeaders } from "../../utils/epics";
import { isProduction } from "../../utils/runtime";
import { PagedResponse, LatestVersionsByFormType } from "./../../utils/types";
import { fetchFormActions, fetchFormsActions, FormDescriptor, loadFormActions, loadFormsActions, removedInaccessibleForms, saveFormActions } from "./actions";
import { resolveRefs } from '../../react/components/form/utils';
import { IDBPDatabase } from 'idb';
import { getLocalDefinition, hasLocalDefinition } from '../../definitions';

/// Syncing Forms

const BACKGROUND_FORM_SYNC_BACKOFF_TIME = 1000 * 60// * 10;   // 10 min

/**
 * Fetching forms is throttled, so prevent form state from having state { fetching: true } unless throttletime has been exceeded.
 */
const syncThrottleEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(throttledSyncForms)),
    throttleTime(BACKGROUND_FORM_SYNC_BACKOFF_TIME),
    filter(_ => {
      const lastCheck = localStorage.getItem(LAST_FORM_CHECK_KEY);
      return (
        !lastCheck ||
        moment(lastCheck)
          .add(BACKGROUND_FORM_SYNC_BACKOFF_TIME, "ms")
          .isBefore(moment())
      );
    }),
    map(fetchFormsActions.request)
  )
})

// get list of evaluation definitions to be fetched
const fetchFormDescriptorsEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchFormsActions.request)),
    exhaustMap(() => {
      const headers = getCommonHeaders(state$);
      return ajax.get(`${config.dfdmHost}/api/definitions`, headers).pipe(
        expand(resp => {
          const response: PagedResponse<FormDescriptor> = resp.response;
          if (!response || !response.next) {
            return empty();
          }
          return ajax.get(response.next, headers);
        }),
        concatMap(res => (res.response as PagedResponse<FormDescriptor>).results),
        reduce((acc: FormDescriptor[], curr) => [...acc, curr], []),
        map(descriptors => {
          // Only latest minor versions should be fetched. No old minor version can ever be created unless
          // all more recent minor versions are 'retired' or 'draft'
          const latestFormVersions: LatestVersionsByFormType = {};
          let formsToFetchAndStore: FormDescriptor[] = [];
    
          const appMajorVersion = Number(appVersion?.split('.')[0]);

          descriptors.forEach(d => {
            const { state, form_id, version_major, version_minor, min_app_version } = d;

            if (state === 'draft' || state === 'retired') {
              return;
            }

            // certain definitions require form components that only exist at or above certain app versions.
            if (!isNaN(appMajorVersion) && min_app_version > appMajorVersion) {
              console.warn(`App version ${appMajorVersion} does not satisfy minimum version for ${form_id} ${version_major}.${version_minor}. Version >= ${min_app_version} is required.`);
              return;
            }

            // Delete any definitions whose major version doesn't satisfy the minimum supported major version and
            // don't have any local evaluations of that major version
            const majorVersionIsSupported = config.versionMapping[form_id]?.min <= version_major;
            const localEvaluations = state$.value.evals.items.filter(evaluation => evaluation.formType === form_id && evaluation.formVersion === version_major);
            if (!majorVersionIsSupported && localEvaluations.size === 0) {
              return;
            }
            
            // definitions in 'testing' state can't be seen on prod
            if (state === 'testing') {
              if (!isProduction) {
                formsToFetchAndStore.push(d);
              }
            } else if (!latestFormVersions[form_id]) {
              latestFormVersions[form_id] = {
                [version_major]: d
              };
            } else if (!latestFormVersions[form_id].hasOwnProperty(version_major)) {
              latestFormVersions[form_id][version_major] = d;
            } else if (latestFormVersions[form_id][version_major].version_minor < version_minor) {
              latestFormVersions[form_id][version_major] = d;
            }
          });
          
          Object.values(latestFormVersions).forEach((formType: { [key: string]: FormDescriptor }) => {
            formsToFetchAndStore = [...formsToFetchAndStore, ...Object.values(formType)];
          })
          
          return formsToFetchAndStore;
        }),
        tap(() => localStorage.setItem(LAST_FORM_CHECK_KEY, moment().toISOString())),
        map(descriptors => fetchFormsActions.success(descriptors))
      );
    }),
    catchError((e: AjaxError) => {
      console.log('error synchronizing forms:', e);
      return of(fetchFormsActions.failure(e));
    })
  );
});

/**
 * Delete minor versions that aren't the latest to not waste disk space.
 */
const removeInaccessibleForms = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(fetchFormsActions.success)),
    pluck("payload"),
    map(descriptors => descriptors.map(d => d.id)),
    exhaustMap(formIds => {

      return from(
        getDB().then(db =>
          db
            .transaction(FORMS_STORE, "readwrite")
            .objectStore(FORMS_STORE)
            .openCursor()
            .then(function checkLocalForm(cursor): Promise<void> | void {
              if (!cursor) {
                return;
              }

              if (formIds.includes(cursor.value.id)) {
                return cursor.continue().then(checkLocalForm);
              }

              return cursor
                .delete()
                .then(() => cursor.continue())
                .then(checkLocalForm);
            })
        )
      )
    }),
    map(() => removedInaccessibleForms())
  );
});

/**
 * Fetch all definitions that are not already persisted locally
 */
const scheduleRemoteFormFetchEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(fetchFormsActions.success)),
    pluck("payload"),
    exhaustMap(descriptors => {
      const remoteDefinitionIds = descriptors.map(desc => desc.id);

      return from(
        getDB().then(db =>
          db
            .transaction(FORMS_STORE, "readonly")
            .objectStore(FORMS_STORE)
            .getAll()
        )
      ).pipe(
        switchMap(localDefinitions => {
          const localDefinitionIds = localDefinitions.map(def => def.id);

          return from(
            remoteDefinitionIds.filter(id => !localDefinitionIds.includes(id))
          )
        }),
        map(id => fetchFormActions.request(id))
      )
    }),
    catchError((e: AjaxError) => of(fetchFormsActions.failure(e)))
  );
});

const fetchRemoteFormsEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(fetchFormActions.request)),
    pluck("payload"),
    mergeMap((formId) => {
      const headers = getCommonHeaders(state$);
      return ajax.get(`${config.dfdmHost}/api/definitions/${formId}`, headers)
    }),
    map(res => res.response as FormDefinitionWrapper),
    filter(res => res.state !== "draft"),
    map(form => saveFormActions.request(form)),
    catchError(e => of(fetchFormActions.failure(e)))
  );
});

const saveFormDefinitionEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(saveFormActions.request)),
    pluck("payload"),
    concatMap((def: FormDefinitionWrapper) => {
      return from(
        getDB()
          .then(db => db.transaction(FORMS_STORE, "readwrite").objectStore(FORMS_STORE))
          .then(store => store.put(def))
      ).pipe(map(() => saveFormActions.success(def.id)));
    }),
    catchError(e => of(saveFormActions.failure(e)))
  );
});

/// Loading Forms

const loadFormsEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(loadFormsActions.request)),
    switchMap(() => {
      return from(
        withRetry_<FormDefinitionWrapper[]>(function(db: IDBPDatabase<DairyFARMDB>) {
          return db.getAll(FORMS_STORE);
        })
      )
    }),
    switchMap((defs: FormDefinitionWrapper[]) => {
      return from(defs).pipe(
        // Not omitting forms for expired certifications to allow user to view in-progress forms with which they cannot proceed
        groupBy(def => def.form_id),
        mergeMap(function getFormVersions(formTypeGroup$) {
          return formTypeGroup$.pipe(
            groupBy(def => def.version_major),
            mergeMap(function getLatestMinorVersions(majorVersionGroup$: any) {
              let allRetired = true;
              return majorVersionGroup$.pipe(
                tap((form: FormDefinitionWrapper) => (allRetired = allRetired && form.state === "retired")),
                toArray(),
                map(function selectCandidate(defs: any) {
                  // if all are retired we need to use latest retired
                  return (
                    (allRetired ? defs : defs.filter((def: any) => def.state !== "retired"))
                      // return highest remaining version
                      .reduce((acc: any, curr: any) => (acc.version_minor > curr.version_minor ? acc : curr))
                  );
                }),
                switchMap(form => {
                  return from(
                    resolveRefs((form as any).definition)
                    .then((definition: any) => ({
                      ...form as any,
                      definition,
                    }))
                    .catch((e: any) => console.error('error resolving refs for definition', e))
                  )
                })
              );
            }) as any,
            reduce(function packageVersions(
              acc: { [version: number]: FormDefinitionWrapper },
              curr: FormDefinitionWrapper
            ): {[version: number]: FormDefinitionWrapper} {
              if (!curr) {
                return acc;
              }
              return {
                ...acc,
                [curr.version_major]: curr,
              };
            },
            {}),
            map(versionMap => ({[formTypeGroup$.key]: versionMap}))
          );
        }),
        reduce(function packageTypes(acc: FormMap, curr) {
          return {...acc, ...curr };
        }, {})
      );
    }),
    map(forms => loadFormsActions.success(forms))
  );
});

const loadFormEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(loadFormActions.request)),
    pluck("payload"),
    switchMap(({ type, version }) =>
      from(
        withRetry_<FormDefinitionWrapper[]>(function(db: IDBPDatabase<DairyFARMDB>) {
          return db
            .transaction(FORMS_STORE)
            .objectStore(FORMS_STORE)
            .index('type')
            .getAll(type)
        })
        .then(
          versions => {
            if (hasLocalDefinition(type)) {
              return getLocalDefinition(type);
            }
            return versions.filter(def => def.version_major === version).sort((a, b) => b.version_minor - a.version_minor)[0];
          }
        )
      ).pipe(
        switchMap(form =>{
          return form
            ? from(
                resolveRefs(form.definition)
                .then((definition: FormDefinition) => ({
                  ...form,
                  definition,
                }))
                .catch((e: any) => console.error('error resolving refs for definition', e))
              ) as any
            : of(form)
        }),
        map(form =>
          form
            ? loadFormActions.success(form as any)
            : loadFormActions.failure(new Error(`form definition ${type}:${version} not found`))
        )
      )
    )
  );
});

// Reload forms
const reloadFormsEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(saveFormActions.success)),
    debounceTime(1000),
    map(() => loadFormsActions.request())
  );
});

export default combineEpics(
  fetchFormDescriptorsEpic,
  scheduleRemoteFormFetchEpic,
  fetchRemoteFormsEpic,
  saveFormDefinitionEpic,
  removeInaccessibleForms,
  loadFormsEpic,
  loadFormEpic,
  reloadFormsEpic,
  syncThrottleEpic
);
